import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import { formatDistanceToNowStrict, parseISO, format } from 'date-fns'

import { ReferenceChange, Decision } from '../interfaces/referenceChanges'

// ── Types ────────────────────────────────────────────────────────────────────

interface StatusChipConfig {
  label: string
  color: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'
  variant?: 'filled' | 'outlined'
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function getRefStatusChipConfig(item: ReferenceChange): StatusChipConfig {
  if (item.decision === null && item.fetch_status === null) {
    return { label: 'Never checked', color: 'default' }
  }
  if (item.fetch_status === 'blocked') {
    return { label: 'Needs manual check', color: 'default' }
  }
  if (item.fetch_status === 'failure' && item.decision === null) {
    return { label: 'Error', color: 'error' }
  }
  switch (item.decision as Decision) {
    case 'no_change':
      return { label: 'No change', color: 'success' }
    case 'material_change':
      return { label: 'Material change', color: 'warning' }
    case 'uncertain':
      return { label: 'Uncertain', color: 'warning', variant: 'outlined' }
    case 'error':
      return { label: 'Error', color: 'error' }
    default:
      return { label: 'Never checked', color: 'default' }
  }
}

// ── Sub-components ───────────────────────────────────────────────────────────

export function RefRelativeTimestamp({ isoString }: { isoString: string }) {
  const utcString = isoString.endsWith('Z') || isoString.includes('+') ? isoString : isoString + 'Z'
  const date = parseISO(utcString)
  const relative = formatDistanceToNowStrict(date, { addSuffix: true })
  const exact = format(date, 'dd MMM yyyy HH:mm:ss')
  return (
    <Tooltip title={exact}>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
        last checked {relative}
      </Typography>
    </Tooltip>
  )
}

export function RefDeltasAccordion({ item }: { item: ReferenceChange }) {
  if (!item.deltas?.items || item.deltas.items.length === 0) return null
  return (
    <Accordion
      disableGutters
      sx={{ mt: 1, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">Evidence ({item.deltas.items.length})</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {item.deltas.items.map((delta, idx) => (
            <Box key={idx}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
              >
                {delta.category}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 0.5 }}>
                <Box sx={{ p: 1, bgcolor: 'error.50', borderRadius: 0.5, border: '1px solid', borderColor: 'error.200' }}>
                  <Typography variant="body2" component="del" sx={{ color: 'error.main', fontStyle: 'normal' }}>
                    {delta.old_quote}
                  </Typography>
                </Box>
                <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 0.5, border: '1px solid', borderColor: 'success.200' }}>
                  <Typography variant="body2" component="ins" sx={{ color: 'success.main', fontStyle: 'normal', textDecoration: 'none' }}>
                    {delta.new_quote}
                  </Typography>
                </Box>
              </Box>
              {delta.explanation && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {delta.explanation}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

// ── Main exported components ─────────────────────────────────────────────────

export interface RefStatusInlineProps {
  item: ReferenceChange | null
}

export function RefStatusInline({ item }: RefStatusInlineProps) {
  if (!item) return null

  const chipConfig = getRefStatusChipConfig(item)
  const isBlocked = item.fetch_status === 'blocked'
  const isStaleFreshFailure = !isBlocked && item.fetch_status === 'failure' && item.decision !== null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
        <Chip
          label={chipConfig.label}
          color={chipConfig.color}
          variant={chipConfig.variant ?? 'filled'}
          size="small"
        />
        {item.last_checked_at && <RefRelativeTimestamp isoString={item.last_checked_at} />}
      </Box>

      {item.decision === 'no_change' && item.first_checked_at && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          Monitoring since <RefRelativeTimestamp isoString={item.first_checked_at} />
        </Typography>
      )}

      {isBlocked && (
        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2">
            This link could not be automatically checked (access denied). Please verify manually.
          </Typography>
        </Alert>
      )}

      {isStaleFreshFailure && (
        <>
          <Alert severity="error" sx={{ mt: 1 }}>
            <Typography variant="body2">
              {item.failure_reason ?? item.review_reason ?? 'Fetch failed'}
            </Typography>
            {item.detected_at && (
              <Typography variant="caption" display="block" sx={{ mt: 0.25 }}>
                Last successful check: <RefRelativeTimestamp isoString={item.detected_at} />
              </Typography>
            )}
          </Alert>
          <Box data-testid="stale-change-box" sx={{ opacity: 0.55, mt: 1 }}>
            {item.summary && <Typography variant="body2">{item.summary}</Typography>}
            <RefDeltasAccordion item={item} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Based on previous successful check
            </Typography>
          </Box>
        </>
      )}

      {!isStaleFreshFailure && item.fetch_status === 'failure' && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <Typography variant="body2">
            {item.failure_reason ?? item.review_reason ?? 'Fetch failed'}
          </Typography>
        </Alert>
      )}

      {!isStaleFreshFailure && (item.decision === 'material_change' || item.decision === 'uncertain') && (
        <>
          {item.summary && <Typography variant="body2" sx={{ mt: 0.75 }}>{item.summary}</Typography>}
          <RefDeltasAccordion item={item} />
        </>
      )}

      {!isStaleFreshFailure && item.decision === 'error' && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <Typography variant="body2">
            {item.failure_reason ?? item.review_reason ?? 'Analysis error'}
          </Typography>
        </Alert>
      )}
    </Box>
  )
}

export interface CheckLinksButtonProps {
  isChecking: boolean
  isDisabled: boolean
  onClick: () => void
}

export function CheckLinksButton({ isChecking, isDisabled, onClick }: CheckLinksButtonProps) {
  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={isChecking ? <CircularProgress size={14} /> : <RefreshIcon />}
      disabled={isDisabled}
      onClick={onClick}
    >
      {isChecking ? 'Checking...' : 'Check links'}
    </Button>
  )
}
